import { useCallback, useState } from 'react';

import { measure } from '@palette.dev/browser';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { getOrderLeverage } from 'actions/orderbook';
import { updateMarginMode, updateTradePreference } from 'actions/trade';
import { getAccountToken, logoutFromSubAccount, switchToMainAccount } from 'actions/user';
import { postMessageToMobileApp } from 'components/app/helpers';
import { ChangeMarginModePopUp } from 'components/placeorder/partials/OrderContainer/leverageSection/ChangeMarginModePopUp';
import Render from 'components/render';
import { RenderForGlobalExchange } from 'components/render/RenderByExchangeType';
import SwitchSubAccountModal from 'components/sub_account/switchSubAccountModal';
import { IS_INDIAN_EXCHANGE, IS_RN_HOME_SCREEN, MARGIN_TYPES } from 'constants/constants';
import { MARGIN_MODE, POST_MESSAGE_TO_MOBILE_EVENTS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import {
  getLoggedUserType,
  getUserAccountNameById,
  noop,
  numberCommaSeparator,
  parseErrorMsg,
  refreshPage,
} from 'helpers/utils';
import { useShowAvailableBalance } from 'hooks/componentHooks/useShowAvailableBalance';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';
import {
  allPrefrencesSelector,
  appStatusSelector,
  userSelector,
} from 'selectors/otherSelectors';
import { selectedProductBalance } from 'selectors/placeOrderSelector';
import { selectedProductState } from 'selectors/tradeSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { Product } from 'types/IProducts';
import IState from 'types/Istore';
import IUser, { IUserKernel, LoggedUserType, Preferences } from 'types/IUser';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';
import MuiSwitch from 'UIKit/MuiSwitch';

import classes from './styles.module.scss';

interface PrevUserType extends IUser {
  token: string;
}

// interface UtilizationBarProps {
//   availableMargin: number;
//   marginUsed: number;
// }

// const UtilizationBar = ({ availableMargin = 0, marginUsed = 0 }: UtilizationBarProps) => {
//   const value = marginUsed / (availableMargin + marginUsed);
//   const percentageText = Number.isNaN(value) ? `0%` : `${round(value * 100, 2)}%`;

//   return (
//     <Box className={classes.utilization}>
//       <Box className={classes.progressBar}>
//         <Box className={classes.progress} style={{ width: percentageText }} />
//       </Box>
//       <Box className={classes.percentage}>{percentageText}</Box>
//     </Box>
//   );
// };

const MarginModeDropdown = ({ showDropdown = false }: { showDropdown: boolean }) => {
  const [showMarginModePopup, setShowMarginModePopup] = useState<boolean>(false);
  const [openSwitchSubAccountModal, setOpenSwitchSubAccountModal] =
    useState<boolean>(false);
  const [isolatedError, setIsolatedError] = useState({});
  const [prevUser, setPrevUser] = useState<PrevUserType | null>(null);
  const { t } = useTranslation(['headerFooter']);
  const user = useSelector(userSelector);
  const dispatch = useAppDispatch();
  const showAvlBalance = useShowAvailableBalance();
  const selectedProduct = (useAppSelector(selectedProductState) ?? {}) as Product;
  const allPreferences = useSelector(state =>
    allPrefrencesSelector(state as IState)
  ) as Preferences;
  const { roundedBalance, balanceSymbol } = useAppSelector(selectedProductBalance);
  const history = useHistory();
  const availableMarginText = numberCommaSeparator(roundedBalance);
  const marginType = user.margin_mode || MARGIN_TYPES.ISOLATED;

  const appStatus = useAppSelector(appStatusSelector);

  // const portfolioMarginSelectorObj = portfolioMarginSelector();
  // const aggregatePortfolioData = portfolioMarginSelectorObj.aggregate || {};
  // const { blocked_margin: blockedMargin } = aggregatePortfolioData;
  // const portfolioMarginBlocked = blockedMargin ? Number(blockedMargin) : 0;
  // const marginUsed = numberCommaSeparator(portfolioMarginBlocked);

  const { id } = user;
  const accountName = getUserAccountNameById(id, user);
  const loggedUserType = getLoggedUserType(user);

  const subAccounts =
    // eslint-disable-next-line no-nested-ternary
    loggedUserType === LoggedUserType.MAIN_ACCOUNT_LOGIN
      ? user.sub_accounts || []
      : loggedUserType === LoggedUserType.SWITCHED_SUB_ACCOUNT
      ? user.main_account?.sub_accounts || []
      : [];

  useDidUpdateEffect(() => {
    const timer = setTimeout(() => {
      if (prevUser?.main_account) {
        logoutFromSubAccount(prevUser?.token).then(() => {
          setPrevUser(null);
          refreshPage();
        });
      } else {
        refreshPage();
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [user.id]);

  const handleAutoMarginChange = useCallback(
    e => {
      const { checked } = e.target;
      dispatch(updateTradePreference({ default_auto_topup: checked }));
    },
    [dispatch]
  );

  const handleMarginModeClose = useCallback(() => {
    setShowMarginModePopup(false);
    const { symbol, contract_type: contractType } = selectedProduct;
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.ORDER_PLACEMENT.MARGIN_REJECT, {
      product_name: symbol,
      product_type: contractType,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetIsolatedError = () => {
    setIsolatedError({});
  };

  const getSelectedProductLeverage = () => {
    dispatch(getOrderLeverage(selectedProduct.id as number));
  };

  const updateMarginModeFun = obj => {
    resetIsolatedError();
    const payload = {
      margin_mode: obj.margin_mode,
      user_id: user?.id,
    } as { margin_mode: MARGIN_MODE; user_id: number };

    dispatch(updateMarginMode(payload))
      .then(() => {
        getSelectedProductLeverage();
        trackMixpanelEvent('Account Margin Mode Changed', {
          account_name: user.account_name,
          margin_mode: obj.margin_mode,
        });
        setShowMarginModePopup(false);
      })
      .catch(err => {
        const { error } = parseErrorMsg(err);
        setIsolatedError({
          code: error?.code,
          context: error?.context,
        });
      });
  };

  const trackMixpanelEventMarginButton = () => {
    const { symbol, contract_type: contractType } = selectedProduct as Product;
    const currentMarginMode =
      marginType === MARGIN_TYPES.ISOLATED
        ? t('headerFooter:marginMode.isolated')
        : t('headerFooter:marginMode.cross');

    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.ORDER_PLACEMENT.MARGIN_BUTTON_CLICK, {
      source: 'Single Order',
      current_margin_mode: currentMarginMode,
      product_type: contractType,
      product_name: symbol,
    });
  };

  const handleChangeMarginClick = e => {
    measure.start('MarginModeDropdown_changeMarginClick');
    e.preventDefault();
    setShowMarginModePopup(state => !state);
    trackMixpanelEventMarginButton();
    measure.end('MarginModeDropdown_changeMarginClick');
  };

  const toggleOpenSwitchSubAccountModal = () => {
    if ((subAccounts?.length ?? 0) > 0) {
      measure.start('MarginModeDropdown_switchSubAccountModal');
      setOpenSwitchSubAccountModal(prev => !prev);
      measure.end('MarginModeDropdown_switchSubAccountModal');
    }
  };

  const switchAccount = (userId: number) => {
    if (userId !== user.id) {
      if (IS_RN_HOME_SCREEN()) {
        postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGOUT);
      }
      setPrevUser({
        token: user.token,
        main_account: user.main_account,
      } as PrevUserType);
      dispatch(
        getAccountToken({
          sub_account_user_id: userId,
        })
      );
    }
  };

  const switchToMainAcc = () => {
    if (user.main_account) {
      if (IS_RN_HOME_SCREEN()) {
        postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGOUT);
      }
      setPrevUser({
        token: user.token,
        main_account: user.main_account,
      } as PrevUserType);
      dispatch(switchToMainAccount());
    }
  };

  const handleSeeMaarginDetailsClick = () => {
    // measure.start('MarginModeDropdown_seeMaarginDetailsClick');
    history.push(routes.portfolio);
  };

  return (
    <>
      <Box renderWhen={showDropdown} className={classes.dropdownPanel}>
        <Box className={classes.topSection}>
          <Box className={classes.accountInUse}>
            <Box className={classes.label}>
              {t('headerFooter:marginMode.accountInUse')}
            </Box>
            <Box className={classes.value}>{accountName}</Box>
          </Box>
          <Box renderWhen={subAccounts.length > 0} className={classes.switchBtn}>
            <DeltaButton
              data-testid="margin-mode-dropdown-switch-sub-accounts"
              onClick={toggleOpenSwitchSubAccountModal}
              text={t('headerFooter:marginMode.switch')}
            />
          </Box>
        </Box>

        <Render when={appStatus !== 'degraded_mode'}>
          <Box className={classes.bottomSection}>
            <Box
              className={cx(classes.values, {
                [classes.marginMode]: marginType === MARGIN_TYPES.ISOLATED,
              })}
            >
              <Box className={classes.label}>
                {t('headerFooter:marginMode.marginMode')}
              </Box>
              <Box className={classes.value}>{marginType}</Box>
            </Box>
            <Render when={marginType === MARGIN_TYPES.ISOLATED}>
              <Box className={cx(classes.values, classes.autoMarginTopUpWrapper)}>
                <Box className={classes.label}>
                  {t('headerFooter:marginMode.autoMarginTopUp')}
                </Box>
                <Box className={classes.value}>
                  <MuiSwitch
                    data-testid="primary-header-auto-topup-switch"
                    onChange={handleAutoMarginChange}
                    checked={allPreferences.default_auto_topup}
                    classes={{
                      thumb: classes.thumb,
                      track: cx(classes.track, {
                        [classes.trackChecked]: allPreferences.default_auto_topup,
                      }),
                      sizeSmall: classes.sizeSmall,
                      switchBase: classes.switchBase,
                      checked: classes.checked,
                    }}
                  />
                </Box>
              </Box>
            </Render>

            <Box className={classes.values}>
              <Box className={classes.label}>
                {t('headerFooter:marginMode.availableMargin')}
              </Box>
              <Box className={classes.value}>
                <Render when={showAvlBalance} else="******">
                  {availableMarginText} {balanceSymbol}
                </Render>
              </Box>
            </Box>
            {/* <Render when={marginType !== MARGIN_TYPES.ISOLATED}>
            <Box className={classes.values}>
              <Box className={classes.label}>
                {t('headerFooter:marginMode.marginUsed')}
              </Box>
              <Box className={classes.value}>
                {marginUsed} {balanceSymbol}
              </Box>
            </Box>
          </Render> */}

            {/* <Render when={marginType !== MARGIN_TYPES.ISOLATED}>
            <Box className={classes.values}>
              <Box className={classes.label}>
                {t('headerFooter:marginMode.utilization')}
              </Box>
              <Box className={classes.value}>
                <UtilizationBar
                  availableMargin={Number(roundedBalance)}
                  marginUsed={marginUsed}
                />
              </Box>
            </Box>
          </Render> */}

            <Box
              className={cx(classes.actionButtons, {
                [classes.actionBtnIsolated]:
                  marginType !== MARGIN_TYPES.PORTFOLIO || IS_INDIAN_EXCHANGE,
              })}
            >
              <Box
                fullWidth={marginType === MARGIN_TYPES.PORTFOLIO && !IS_INDIAN_EXCHANGE}
                className={classes.changeMarginModeBtn}
              >
                <DeltaButton
                  fullWidth
                  data-testid="margin-mode-dropdown-change-margin-mode-button"
                  onClick={handleChangeMarginClick}
                  text={t('headerFooter:marginMode.changeMarginMode')}
                  onKeyPress={noop}
                />
              </Box>
              <RenderForGlobalExchange>
                <Render when={marginType === MARGIN_TYPES.PORTFOLIO}>
                  <Box fullWidth className={classes.seemarginDetailsBtn}>
                    <DeltaButton
                      fullWidth
                      onKeyPress={noop}
                      data-testid="margin-mode-dropdown-see-margin-details"
                      onClick={handleSeeMaarginDetailsClick}
                      text={t('headerFooter:marginMode.seemarginDetails')}
                    />
                  </Box>
                </Render>
              </RenderForGlobalExchange>
            </Box>
          </Box>
        </Render>
      </Box>

      <Render when={showMarginModePopup}>
        <ChangeMarginModePopUp
          showModal={showMarginModePopup}
          handleMarginModeClose={handleMarginModeClose}
          updateMarginModeFun={updateMarginModeFun}
          marginType={marginType}
          isolatedError={isolatedError}
          resetIsolatedError={resetIsolatedError}
        />
      </Render>

      <Render when={openSwitchSubAccountModal}>
        <SwitchSubAccountModal
          isModalOpen={openSwitchSubAccountModal}
          handleCloseModal={toggleOpenSwitchSubAccountModal}
          subAccounts={subAccounts as IUserKernel[]}
          user={user}
          switchToMainAcc={switchToMainAcc}
          switchAccount={switchAccount}
        />
      </Render>
    </>
  );
};

export default MarginModeDropdown;
